import {Autocomplete, CircularProgress, Divider, FormHelperText, Grid, TextField} from "@mui/material";
import {useMemo, useRef, useState} from "react";
import {useGetAllBundlesQuery, useGetAllCampaignsQuery, useGetTagsQuery} from "../../../core/reducers/campaign";
import {getEnvName} from "../../Utils/CommonUtils";
import {MODE} from "../campaignMode";
import {useSearchParams} from "react-router-dom";
import {useSelector} from "react-redux";

const loadingAdornment = (loading, params) => (
    <>
        {loading ? <CircularProgress color="inherit" size={18}/> : null}
        {params.InputProps.endAdornment}
    </>
);

export default function BasicStep(props) {
    const {currentCampaign, types, errorFields, mode} = props;
    const [searchParams] = useSearchParams();
    const sourceMode = useSelector((state) => state.data.SourceMode);
    const env = getEnvName('env', sourceMode, searchParams);
    const variantsMode = searchParams.get('mode') == 'variants';

    const {data: bundles, isFetching, isLoading} = useGetAllBundlesQuery(env);
    const {data: tagsData, isLoading: isLoadingTags} = useGetTagsQuery(env);
    const {data: campaigns, isLoading: isLoadingCampaigns} = useGetAllCampaignsQuery({env}, {skip: !props.showPreviousCampaignId});

    const abbreviations = useMemo(() => Object.fromEntries(
        Object.entries(types || {}).filter(([, type]) => type.metaData?.abbreviation).map(([key, type]) => [key, type.metaData.abbreviation])
    ), [types]);
    const typeLabel = (option) => option + (abbreviations[option] ? ' (' + abbreviations[option] + ')' : '');

    const selectedType = types?.[currentCampaign.offerType];
    const interactionType = selectedType ? selectedType.interactionType : '';
    const matchmakingTypes = selectedType?.matchmakingTypes ?? [];
    const showMatchmaking = ['TOURNAMENT', 'TEAM_TOURNAMENT'].includes(interactionType) && matchmakingTypes.length > 0;

    const filteredBundles = useMemo(() => (bundles && currentCampaign.offerType)
        ? bundles.filter(bundle => bundle.campaignType === currentCampaign.offerType).map(bundle => bundle.id).sort()
        : [], [bundles, currentCampaign.offerType]);

    const previousCampaignOptions = campaigns?.campaigns
        ?.filter(campaign => campaign.type === currentCampaign.offerType && campaign.id !== currentCampaign.campaignId)
        .map(campaign => campaign.id)
        .sort() || [];

    // ---- теги: выбор из списка, Enter / "," / blur добавляют введённый тег ----
    const tags = tagsData == undefined ? [] : tagsData.tags;
    const [selectedTags, setSelectedTags] = useState(currentCampaign.tags ? currentCampaign.tags.map(tag => tag.trim()) : []);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef(null);

    const commitTags = (nextTags) => {
        const cleanedTags = nextTags.map(tag => tag.trim()).filter(tag => tag !== '');
        setSelectedTags(cleanedTags);
        props.handleChange({target: {name: 'tags', value: cleanedTags}});
    };
    const handleTagAddition = () => {
        const newTag = inputValue.trim();
        if (!newTag) return;
        commitTags([...selectedTags, newTag]);
        if (inputRef.current) inputRef.current.value = '';
        setInputValue('');
    };
    const handleTagKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            handleTagAddition();
        }
    };

    const idError = errorFields.has('campaignId') || errorFields.has('campaignIdRequired') || errorFields.has('regexp')
        || currentCampaign.campaignId?.includes('(Clone)');

    return (
        <Grid container spacing={2}>
            {types &&
                <Grid item xs={12}>
                    <Autocomplete
                            disabled={variantsMode || mode === MODE.UPDATE}
                        options={Object.keys(types).sort()}
                        getOptionLabel={typeLabel}
                        value={currentCampaign.offerType ? currentCampaign.offerType : null}
                        onChange={(event, value) => props.handleOfferTypeChange(value, types)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                id="offerType"
                                name="offerType"
                                label="Type *"
                                error={errorFields.has('offerType')}
                            />
                        )}
                    />
                </Grid>}

            {bundles &&
                <Grid item xs={12}>
                    <Autocomplete
                            disabled={variantsMode}
                        options={filteredBundles}
                        onChange={(event, value) => props.handleBundleChange(value)}
                        value={currentCampaign.bundleId}
                        noOptionsText={currentCampaign.offerType ? 'No bundles for this type' : 'Choose a type first'}
                        renderInput={params => (
                            <TextField
                                {...params}
                                id="bundleId"
                                name="bundleId"
                                label="Bundle ID *"
                                InputProps={{...params.InputProps, endAdornment: loadingAdornment(isLoading || isFetching, params)}}
                            />
                        )}
                    />
                </Grid>}

            {/* отделяет обязательный выбор типа/бандла от остальных полей */}
            <Grid item xs={12}>
                <Divider component="hr"/>
            </Grid>

            {showMatchmaking &&
                <Grid item xs={12}>
                    <Autocomplete
                            options={matchmakingTypes}
                        value={currentCampaign.matchmakingType ? currentCampaign.matchmakingType : null}
                        onChange={(event, value) => props.handleMatchmakingTypeChange(event, value)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                name="matchmakingType"
                                label="Matchmaking Type *"
                                error={errorFields.has('matchmakingType')}
                            />
                        )}
                    />
                </Grid>}

            {interactionType === 'TEAM_TOURNAMENT' &&
                <Grid item xs={12}>
                    <TextField
                            required
                        id="tournamentId"
                        name="tournamentId"
                        label="Tournament Id"
                        error={errorFields.has('tournamentId')}
                        fullWidth
                        value={currentCampaign.tournamentId ?? ''}
                        onChange={props.handleChange}
                    />
                </Grid>}

            {props.showPreviousCampaignId &&
                <Grid item xs={12}>
                    <Autocomplete
                            freeSolo
                        autoSelect
                        loading={isLoadingCampaigns}
                        options={previousCampaignOptions}
                        value={currentCampaign.previuosCampaignId || null}
                        onChange={(event, value) => props.handleChange({target: {name: 'previuosCampaignId', value: value || ""}})}
                        renderInput={params => (
                            <TextField {...params} id="previuosCampaignId" name="previuosCampaignId" label="Previous Campaign ID"/>
                        )}
                    />
                </Grid>}

            <Grid item xs={12}>
                <TextField
                    required
                    disabled={mode === MODE.UPDATE}
                    id="campaignId"
                    name="campaignId"
                    label="Campaign ID"
                    error={idError}
                    fullWidth
                    value={currentCampaign.campaignId}
                    onChange={props.handleChange}
                />
                {errorFields.has('regexp') && <FormHelperText error>
                    Campaign ID must match <a href={"https://belka-games.atlassian.net/wiki/spaces/BAC/pages/4008345627/Mephisto+Naming+Rules"} target={"_blank"} rel="noreferrer">Mephisto Naming Rules</a>
                </FormHelperText>}
                {errorFields.has('campaignId') && <FormHelperText error>This id is already taken</FormHelperText>}
                {currentCampaign.campaignId?.includes('(Clone)') && <FormHelperText error>
                    Change Campaign ID, it shouldn't contain "Clone"
                </FormHelperText>}
            </Grid>
            <Grid item xs={12}>
                <TextField
                    id="groupId"
                    name="groupId"
                    label="Group ID"
                    value={currentCampaign.groupId}
                    fullWidth
                    onChange={props.handleChange}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    required
                    id="priority"
                    name="priority"
                    label="Priority"
                    error={errorFields.has('priority')}
                    helperText={errorFields.has('priority') ? 'Digits only' : undefined}
                    value={currentCampaign.priority}
                    fullWidth
                    onChange={props.handleChange}
                />
            </Grid>

            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    freeSolo
                    id="tags-autocomplete"
                    options={isLoadingTags ? [] : (tags || [])}
                    value={selectedTags}
                    onChange={(event, newTags) => commitTags(newTags)}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                    loading={isLoadingTags}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tags (Enter or , to add)"
                            inputRef={inputRef}
                            error={errorFields.has('tags')}
                            onKeyDown={handleTagKeyDown}
                            onBlur={handleTagAddition}
                            InputProps={{...params.InputProps, endAdornment: loadingAdornment(isLoadingTags, params)}}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
}
